import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollReveal, Marquee } from "@/components/ScrollAnimations";

export const ReviewsSection = () => {
  const { user, token } = useAuth();
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = () => {
    axios.get(`${API}/reviews`).then((res) => {
      setReviews(res.data.reviews || []);
      setEnabled(res.data.enabled !== false);
    }).catch(() => {});
  };

  const submitReview = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { rating, text }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Review submitted!");
      setText("");
      setRating(5);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (!enabled) return null;

  return (
    <section id="reviews" className="py-24 px-6 relative overflow-hidden" data-testid="reviews-section">

      <div
        className="absolute bottom-0 left-0 w-96 h-96 opacity-20 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(217, 70, 239, 0.15), transparent 70%)",
        }}
      />

      <Marquee speed={35} className="py-3 border-y border-white/5 mb-16">
        {[...Array(12)].map((_, i) => (
          <span key={`marquee-row-${i}`} className="flex items-center mx-6">
            {[...Array(5)].map((_, j) => (
              <Star key={`marquee-star-${i}-${j}`} className="w-3 h-3 text-purple-500/30 fill-purple-500/30 mx-0.5" />
            ))}
          </span>
        ))}
      </Marquee>

      <div className="max-w-7xl mx-auto">
        <ScrollReveal direction="right" duration={900}>
          <p
            className="text-xs tracking-[0.3em] uppercase font-bold text-purple-400 mb-3"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            {t("reviews_subtitle")}
          </p>
        </ScrollReveal>
        <ScrollReveal direction="right" delay={100} duration={900}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-16" data-testid="reviews-title">
            {t("reviews_title")}
          </h2>
        </ScrollReveal>

        {reviews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {reviews.map((review, idx) => (
              <ScrollReveal
                key={review.id || idx}
                direction={idx % 3 === 0 ? "left" : idx % 3 === 1 ? "up" : "right"}
                delay={idx * 100}
                duration={800}
              >
                <div
                  className="glass-card p-6 rounded-xl hover:border-purple-500/20 transition-all duration-500 h-full"
                  data-testid={`review-card-${idx}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={`review-${review.id || idx}-star-${i}`}
                        className={`w-4 h-4 ${
                          i < review.rating ? "text-purple-400 fill-purple-400" : "text-neutral-700"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-neutral-300 leading-relaxed mb-4">"{review.text}"</p>
                  <p className="text-xs text-neutral-500 tracking-wider uppercase">
                    {review.user_name}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        ) : (
          <ScrollReveal direction="up">
            <p className="text-neutral-500 mb-16" data-testid="no-reviews">{t("reviews_no_exp")}</p>
          </ScrollReveal>
        )}

        {user && !user.is_admin && (
          <ScrollReveal direction="up" delay={200}>
            <div className="glass-card p-8 rounded-xl max-w-lg" data-testid="review-form">
              <h3 className="text-lg font-semibold mb-4">{t("reviews_leave")}</h3>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="transition-transform duration-200 hover:scale-110"
                    data-testid={`review-star-${star}`}
                  >
                    <Star
                      className={`w-6 h-6 ${
                        star <= rating ? "text-purple-400 fill-purple-400" : "text-neutral-700 hover:text-neutral-500"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("reviews_share")}
                className="bg-black/50 border-white/10 focus:border-purple-500/50 text-white placeholder:text-neutral-600 mb-4 min-h-[100px]"
                data-testid="review-text-input"
              />
              <Button
                onClick={submitReview}
                disabled={submitting || !text.trim()}
                className="bg-purple-600 hover:bg-purple-500 text-white font-bold border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs"
                data-testid="review-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? t("reviews_submitting") : t("reviews_submit")}
              </Button>
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
};
